import { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { COMMON } from "../constants/common";

/**
 * Decodes a JWT token without verification
 * Returns the payload as an object
 */
const decodeJwtToken = (token: string): any => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Checks if a token is expired or will expire soon (within 5 minutes)
 * @param token - JWT token string
 * @returns true if token is expired or will expire within 5 minutes
 */
export const isTokenExpiredOrExpiringSoon = (token: string | null): boolean => {
  if (!token) return true;

  try {
    const decoded = decodeJwtToken(token);
    if (!decoded || !decoded.exp) return true;

    // Get expiration time (exp is in seconds)
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes buffer

    // Token is expired or will expire within 5 minutes
    return expirationTime <= currentTime + fiveMinutesInMs;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // Assume expired if we can't check
  }
};

/**
 * Refreshes the access token using MSAL
 * @param msalInstance - MSAL IPublicClientApplication instance
 * @param account - User account info
 * @returns Promise<string> - New access token
 * @throws Error if token refresh fails
 */
export const refreshAccessToken = async (
  msalInstance: IPublicClientApplication,
  account: AccountInfo | null
): Promise<string> => {
  if (!account) {
    throw new Error("No account available for token refresh");
  }

  try {
    console.log("refreshAccessToken");
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });

    if (response && response.accessToken) {
      // Update token in localStorage and return it
      localStorage.setItem(COMMON.ACCESS_TOKEN, response.accessToken);
      return response.accessToken;
    } else {
      throw new Error("Token refresh failed: No access token in response");
    }
  } catch (error: any) {
    console.error("Token refresh error:", error);
    
    // If silent token acquisition fails, we need to redirect to login
    // This could be due to:
    // - User needs to re-authenticate
    // - Token refresh token expired
    // - Network issues
    throw new Error(
      error.message || "Failed to refresh access token. Please login again."
    );
  }
};
